import { useState, useEffect, useCallback } from "react";
import { useAccessControl } from "@/hooks/useAccessControl";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Pagination } from "@/components/ui/pagination";
import { EmptyState } from "@/components/ui/empty-state";
import {
  InvoiceStatusBadge,
  invoiceTitle,
  tenantInvoiceAmount,
  patronLabel,
  dateCell,
  CURRENCY_SYMBOL,
} from "@/components/invoice/InvoiceHelpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { invoicesApi, type AdminInvoice } from "@/lib/api/invoices";
import {
  Loader2,
  Plus,
  Receipt,
  Search,
  FileText,
  User,
  Tag,
  DollarSign,
  CalendarDays,
  AlignLeft,
  CheckCircle2,
} from "lucide-react";

const PAGE_SIZE = 20;

export function ManageInvoicesPage() {
  const { hasPermission } = useAccessControl();
  const canView = hasPermission("invoices.view");
  const canManage = hasPermission("invoices.manage");

  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [userIdFilter, setUserIdFilter] = useState("");

  const [reqCursor, setReqCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([]);
  const [nextPageCursor, setNextPageCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState("");
  const [patronSearch, setPatronSearch] = useState("");
  const [patrons, setPatrons] = useState<Array<{ id: string; email: string; name: string }>>([]);
  const [patronSearching, setPatronSearching] = useState(false);
  const [selectedPatronLabel, setSelectedPatronLabel] = useState("");

  const [form, setForm] = useState({
    userId: "",
    type: "custom",
    amountDue: 5000,
    description: "",
    dueDate: "",
  });

  const currentPage = cursorStack.length + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadInvoices = useCallback(
    async (cursorArg: string | null) => {
      try {
        setLoading(true);
        const res = await invoicesApi.listAdmin({
          status: statusFilter && statusFilter !== "all" ? statusFilter : undefined,
          userId: userIdFilter || undefined,
          limit: PAGE_SIZE,
          cursor: cursorArg ?? undefined,
        });
        setInvoices(res.invoices);
        setTotal(res.total);
        setNextPageCursor(res.nextCursor);
        setHasMore(res.hasMore);
        setReqCursor(cursorArg);
      } catch (error) {
        console.error("Failed to load invoices:", error);
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, userIdFilter]
  );

  useEffect(() => {
    if (!canView) return;
    setCursorStack([]);
    void loadInvoices(null);
  }, [canView, statusFilter, userIdFilter, loadInvoices]);

  useEffect(() => {
    if (!patronSearch.trim()) {
      setPatrons([]);
      return;
    }
    const t = setTimeout(() => {
      setPatronSearching(true);
      invoicesApi
        .searchPatrons(patronSearch)
        .then(setPatrons)
        .catch(() => setPatrons([]))
        .finally(() => setPatronSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [patronSearch]);

  const resetForm = () => {
    setForm({
      userId: "",
      type: "custom",
      amountDue: 5000,
      description: "",
      dueDate: "",
    });
    setPatronSearch("");
    setSelectedPatronLabel("");
    setPatrons([]);
    setCreateError("");
  };

  const handleCreate = async () => {
    if (!form.userId || form.amountDue <= 0) {
      setCreateError("Please select a patron and enter a valid amount.");
      return;
    }
    try {
      setCreateSubmitting(true);
      setCreateError("");
      await invoicesApi.create({
        userId: form.userId,
        type: form.type,
        amountDue: form.amountDue,
        description: form.description || undefined,
        dueDate: form.dueDate || undefined,
      });
      setShowCreateDialog(false);
      resetForm();
      setCursorStack([]);
      void loadInvoices(null);
    } catch (error: any) {
      setCreateError(error.response?.data?.message || "Failed to create invoice");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handlePrintReceipt = (invoiceId: string) => {
    window.open(`/admin/invoices/${invoiceId}/receipt`, "_blank", "noopener,noreferrer");
  };

  if (!canView && !canManage) {
    return (
      <EmptyState
        icon={<FileText className="h-6 w-6 text-slate-400" />}
        title="No access"
        description="You do not have permission to manage invoices."
      />
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Manage Invoices"
        description="View patron invoices, create fines or custom charges, and open receipts."
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Manage Invoices" }]}
        showSearch={false}
        titleSize="md"
        actions={
          canManage ? (
            <Button
              className="min-h-11 w-full shrink-0 touch-manipulation sm:min-h-10 sm:w-auto"
              onClick={() => setShowCreateDialog(true)}
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden />
              Create invoice
            </Button>
          ) : undefined
        }
      />

      {/* Filter toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm tabular-nums text-slate-500">
          {total} invoice{total !== 1 ? "s" : ""} total
        </p>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="min-h-11 w-full touch-manipulation sm:min-h-9 sm:w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Filter by user ID"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            className="min-h-11 w-full touch-manipulation sm:min-h-9 sm:w-[220px]"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex min-h-[200px] items-center justify-center py-12" role="status">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" aria-hidden />
          <span className="sr-only">Loading invoices</span>
        </div>
      ) : invoices.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-6 w-6 text-slate-400" />}
          title="No invoices found"
          description={
            statusFilter !== "all" || userIdFilter
              ? "Try adjusting your filters to find what you're looking for."
              : "Invoices will appear here once they are created for patrons."
          }
          action={
            canManage ? (
              <Button variant="outline" size="sm" onClick={() => setShowCreateDialog(true)}>
                <Plus className="mr-2 h-4 w-4" aria-hidden />
                Create first invoice
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {/* Desktop / tablet table */}
          <div className="hidden overflow-x-auto rounded-lg border border-slate-200 sm:block">
            <table className="w-full min-w-[760px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/80">
                  <th className="w-12 whitespace-nowrap px-3 py-3 text-center font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                    S/N
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Invoice
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Patron
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-right font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Amount
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Date
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-right font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {invoices.map((inv, idx) => {
                  const sn = (currentPage - 1) * PAGE_SIZE + idx + 1;
                  return (
                    <tr key={inv.id} className="hover:bg-slate-50/60">
                      <td className="whitespace-nowrap px-3 py-3 text-center tabular-nums text-slate-400">
                        {sn}
                      </td>
                      <td className="max-w-[220px] px-4 py-3 align-top">
                        <span className="font-medium text-slate-900" title={invoiceTitle(inv)}>
                          {invoiceTitle(inv)}
                        </span>
                      </td>
                      <td className="max-w-[240px] px-4 py-3 align-top text-slate-600">
                        <span className="line-clamp-2 break-words" title={patronLabel(inv)}>
                          {patronLabel(inv)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top text-right font-medium tabular-nums text-slate-900">
                        {CURRENCY_SYMBOL}
                        {tenantInvoiceAmount(inv).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <InvoiceStatusBadge status={inv.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top text-slate-500">
                        {dateCell(inv)}
                      </td>
                      <td className="px-4 py-3 align-middle text-right">
                        {inv.status === "paid" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="touch-manipulation"
                            onClick={() => handlePrintReceipt(inv.id)}
                          >
                            <Receipt className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                            Receipt
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile list */}
          <ul className="space-y-3 sm:hidden" role="list">
            {invoices.map((inv, idx) => {
              const sn = (currentPage - 1) * PAGE_SIZE + idx + 1;
              return (
                <li
                  key={inv.id}
                  className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                >
                  <div className="flex items-start justify-between gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium tabular-nums text-slate-400">
                          {sn}.
                        </span>
                        <p className="font-heading text-sm font-semibold leading-snug text-slate-900">
                          {invoiceTitle(inv)}
                        </p>
                      </div>
                      <p className="mt-1 line-clamp-1 text-xs text-slate-500">{patronLabel(inv)}</p>
                    </div>
                    <InvoiceStatusBadge status={inv.status} />
                  </div>
                  <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5 text-sm">
                    <div>
                      <span className="font-semibold tabular-nums text-slate-900">
                        {CURRENCY_SYMBOL}
                        {tenantInvoiceAmount(inv).toLocaleString()}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">{dateCell(inv)}</span>
                  </div>
                  {inv.status === "paid" && (
                    <div className="border-t border-slate-100 px-4 py-2.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10 w-full touch-manipulation"
                        onClick={() => handlePrintReceipt(inv.id)}
                      >
                        <Receipt className="mr-2 h-4 w-4" aria-hidden />
                        Open receipt
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Pagination */}
          <Pagination
            mode="cursor"
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={total}
            itemsPerPage={PAGE_SIZE}
            hasMore={hasMore}
            loading={loading}
            onFirst={() => {
              setCursorStack([]);
              void loadInvoices(null);
            }}
            onPrev={() => {
              if (cursorStack.length === 0) return;
              const prev = cursorStack[cursorStack.length - 1];
              setCursorStack((s) => s.slice(0, -1));
              void loadInvoices(prev);
            }}
            onNext={() => {
              if (!nextPageCursor) return;
              setCursorStack((s) => [...s, reqCursor]);
              void loadInvoices(nextPageCursor);
            }}
          />
        </>
      )}

      {/* ── Create Invoice Dialog ──────────────────────────────── */}
      <Dialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="flex max-h-[min(90vh,720px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
          {/* Header */}
          <DialogHeader className="shrink-0 border-b border-slate-100 px-6 pb-4 pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="font-heading text-lg font-semibold text-slate-900">
                  Create Invoice
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-sm text-slate-500">
                  Charge a patron for fines, fees, or custom services.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Body */}
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {createError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {createError}
              </div>
            )}

            {/* Patron search */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                <User className="h-3.5 w-3.5 text-slate-400" />
                Patron
                <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by name or email…"
                  value={patronSearch}
                  onChange={(e) => {
                    setPatronSearch(e.target.value);
                    if (form.userId) {
                      setForm((f) => ({ ...f, userId: "" }));
                      setSelectedPatronLabel("");
                    }
                  }}
                  className="pl-9"
                />
              </div>
              {patronSearching && (
                <div className="flex items-center gap-2 px-1 text-xs text-slate-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Searching…
                </div>
              )}
              {patrons.length > 0 && (
                <div className="max-h-36 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                  {patrons.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors hover:bg-slate-50 ${
                        form.userId === p.id ? "bg-primary/5 text-primary" : "text-slate-700"
                      }`}
                      onClick={() => {
                        const label = p.name ? `${p.name} (${p.email})` : p.email;
                        setForm((f) => ({ ...f, userId: p.id }));
                        setPatronSearch(p.name || p.email);
                        setSelectedPatronLabel(label);
                        setPatrons([]);
                      }}
                    >
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-600">
                        {(p.name || p.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{p.name || p.email}</p>
                        {p.name && <p className="truncate text-xs text-slate-400">{p.email}</p>}
                      </div>
                      {form.userId === p.id && (
                        <CheckCircle2 className="ml-auto h-4 w-4 shrink-0 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              )}
              {form.userId && selectedPatronLabel && patrons.length === 0 && (
                <p className="flex items-center gap-1.5 px-1 text-xs text-emerald-600">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {selectedPatronLabel}
                </p>
              )}
            </div>

            {/* Type + Amount row */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <Tag className="h-3.5 w-3.5 text-slate-400" />
                  Type
                </Label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="registration">Registration</SelectItem>
                    <SelectItem value="fine">Fine / Late fee</SelectItem>
                    <SelectItem value="custom">Custom charge</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <DollarSign className="h-3.5 w-3.5 text-slate-400" />
                  Amount ({CURRENCY_SYMBOL})<span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  min={1}
                  value={form.amountDue}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      amountDue: Number(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                <AlignLeft className="h-3.5 w-3.5 text-slate-400" />
                Description
                <span className="text-xs font-normal text-slate-400">(optional)</span>
              </Label>
              <Textarea
                placeholder="e.g. Late return fee for 'Introduction to Algorithms', processing fee…"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="resize-none"
              />
            </div>

            {/* Due date */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
                Due date
                <span className="text-xs font-normal text-slate-400">(optional)</span>
              </Label>
              <Input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
              />
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="flex-col gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="min-h-11 w-full touch-manipulation sm:min-h-10 sm:w-auto"
              onClick={() => {
                setShowCreateDialog(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              className="min-h-11 w-full touch-manipulation sm:min-h-10 sm:w-auto"
              onClick={handleCreate}
              disabled={createSubmitting}
            >
              {createSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Creating…
                </>
              ) : (
                "Create invoice"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
